/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import ContentfulError, { ERROR_MESSAGES } from 'bundles/coursera-plus-landing-page/components/ContentfulError';
import HeroDesignWithNoImage from 'bundles/coursera-plus-landing-page/components/heroContentful/design/HeroDesignWithNoImage';
import YellowDots from 'bundles/coursera-plus-landing-page/components/heroContentful/design/YellowDots';
import YesICan from 'bundles/coursera-plus-landing-page/components/heroContentful/design/YesICan';
import TestDesign from 'bundles/coursera-plus-landing-page/components/heroContentful/flexibleDesign/TestDesign';
import { heroDesignChoices } from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

type PropsFromCaller = {
  isCourseraLiteContentfulPage: boolean;
};

type PropsFromStores = {
  userAgent?: UserAgentInfo;
};

type Props = PropsFromCaller & PropsFromStores;

export type StaticComponentProps = {
  imageUrl?: string;
  slug?: string;
} & Props;

type DesignComponent = {
  component: React.ComponentType<StaticComponentProps>;
  isFlexibleDesign: boolean;
};

const getDesignComponent = (choice: string): DesignComponent | null => {
  switch (choice) {
    case heroDesignChoices.YES_I_CAN: {
      return {
        component: YesICan,
        isFlexibleDesign: false,
      };
    }
    case heroDesignChoices.YELLOW_DOTS: {
      return {
        component: YellowDots,
        isFlexibleDesign: false,
      };
    }
    case heroDesignChoices.TEST_DESIGN: {
      return {
        component: TestDesign,
        isFlexibleDesign: true,
      };
    }
    case heroDesignChoices.NO_HERO_IMAGE: {
      return {
        component: HeroDesignWithNoImage,
        isFlexibleDesign: false,
      };
    }
    default:
      return null;
  }
};

const Hero = ({ userAgent, isCourseraLiteContentfulPage }: Props) => {
  const landingPageExtended = useLandingPageExtended();
  const landingPage = landingPageExtended.landingPage;
  const { heroDesignChoice } = landingPage;
  const error = <ContentfulError errorMessage={ERROR_MESSAGES.invalidDesign} />;
  if (!heroDesignChoice) {
    return error;
  }
  const designComponent = getDesignComponent(heroDesignChoice);
  if (!designComponent) {
    return error;
  }
  if (!designComponent.isFlexibleDesign) {
    const StaticComponent = designComponent.component as React.ComponentType<StaticComponentProps>;
    return (
      <div data-e2e="coursera-plus-hero">
        <StaticComponent
          slug={landingPage.slug ?? undefined}
          imageUrl={landingPage.heroImage?.url ?? undefined}
          userAgent={userAgent}
          isCourseraLiteContentfulPage={isCourseraLiteContentfulPage}
        />
      </div>
    );
  } else {
    const NewComponent = designComponent.component as React.ComponentType<{}>;
    return <NewComponent />;
  }
};

export default connectToStores<Props, PropsFromCaller>([ApplicationStoreClass], (ApplicationStore, props) => ({
  ...props,
  userAgent: ApplicationStore.getUserAgent(),
}))(Hero);
